import axios from 'axios'
import {DOC_FIELD_UPDATE, DOC_FILL_SUPPLIER, DOC_LINE_ALTERNATIVE_ALERT, DOC_LINES_BATCH_UPDATE} from '../index'
import {forceToNumber} from '@/utils'
import {withConfirmation} from '@/decorators/withConfirmation'
import {
    MatchingExtensionAutofill,
    MatchingExtensionProcessLevel,
    MatchingExtensionTypes
} from '@/types/document-viewer/extension/matching'
import {
    buildDocumentLineMatchTo,
    checkIsLinesMatched,
    getDocLineQtyToMatch,
    getMatchingItemLineRemainingQty
} from './index'
import {AppDocLineTypes} from '@/types/document/lines'
import {documentApi} from '@/api/document'
import {isPlainObject} from 'lodash'

export const DOC_PO_SET = 'DOC_PO_SET'
export const DOC_PO_CHECK = 'DOC_PO_CHECK'
export const DOC_PO_ON_MATCH = 'DOC_PO_ON_MATCH'
export const DOC_PO_ON_UNMATCH = 'DOC_PO_ON_UNMATCH'
export const DOC_PO_ON_MATCH_LINE = 'DOC_PO_ON_MATCH_LINE'
export const DOC_PO_ADJUST_LINE_PO = 'DOC_PO_ADJUST_LINE_PO'
export const DOC_PO_AUTOMATCH = 'DOC_PO_AUTOMATCH'
export const DOC_PO_AUTOMATCH_LINES = 'DOC_PO_AUTOMATCH_LINES'
export const DOC_PO_SET_IS_MATCHING = 'DOC_PO_SET_IS_MATCHING'
export const DOC_PO_SET_IS_AUTO_MATCHING = 'DOC_PO_SET_IS_AUTO_MATCHING'
export const DOC_PO_RUN_DATA_OVERRIDE = 'DOC_PO_RUN_DATA_OVERRIDE'
export const DOC_PO_RUN_DATA_OVERRIDE_FOR_LINE = 'DOC_PO_RUN_DATA_OVERRIDE_FOR_LINE'

export const DOC_PO_FETCH_TOTAL_ALLOCATED = 'DOC_PO_FETCH_TOTAL_ALLOCATED'
export const DOC_PO_SET_TOTAL_ALLOCATED = 'DOC_PO_SET_TOTAL_ALLOCATED'
export const DOC_PO_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL = 'DOC_PO_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL'
export const DOC_PO_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED = 'DOC_PO_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED'

export const DOC_PO_RESET = 'DOC_PO_RESET'

export const state = {
    hasTotalAllocatedGreaterThanDocTotal: false,
    hasTotalUnallocatedGreaterThanAllowed: false,

    purchaseOrder: null,
    isPoMatching: false,
    isPoAutoMatching: false,

    poTotalAllocated: 0
}

export const getters = {
    hasTotalAllocatedGreaterThanDocTotal: state => state.hasTotalAllocatedGreaterThanDocTotal,
    hasTotalUnallocatedGreaterThanAllowed: state => state.hasTotalUnallocatedGreaterThanAllowed,

    purchaseOrder: state => state.purchaseOrder,
    isPoMatching: state => state.isPoMatching,
    isPoAutoMatching: state => state.isPoAutoMatching,

    getLineMatchedQty: () => line => _.sumBy(line.ordered, x => forceToNumber(x.qty)) || 0,

    hasAllPoLinesMatched: (state, getters, rootState, rootGetters) => {
        if (!rootGetters['doc_viewer_settings/poCheckEnabled'] || !rootGetters['doc_viewer/lines']) {
            return true
        }
        for (const line of rootGetters['doc_viewer/notDeletedLines'].filter(l => l.type === AppDocLineTypes.ITEM)) {
            if (getDocLineQtyToMatch(line) > getters.getLineMatchedQty(line)) {
                return false
            }
        }
        return true
    },

    getNotMatchedPoQtyForLine: (state, getters, rootState, rootGetters) => line => {
        const qty = getDocLineQtyToMatch(line)
        if (!rootGetters['doc_viewer_settings/poCheckEnabled'] || !getters.purchaseOrder || !qty) {
            return 0
        }
        if (qty - getters.getLineMatchedQty(line) > 0) {
            return qty - getters.getLineMatchedQty(line)
        }
        return 0
    },

    // TODO: remove all doc-level getters
    poTotal: state => state.purchaseOrder ? forceToNumber(state.purchaseOrder.total) : 0,
    poTotalAllocated: state => forceToNumber(state.poTotalAllocated),
    poAmountRemaining: (state, getters) => getters.poTotal - getters.poTotalAllocated,
    docTotalGreaterThanPoTotal: (state, getters, rootState, rootGetters) => {
        if (!getters.purchaseOrder ||
            !rootGetters['doc_viewer/doc'] ||
            !rootGetters['doc_viewer_settings/poTotalDeviationCheckRequiredToProcess']) {
            return false
        }
        return forceToNumber(rootGetters['doc_viewer/doc'].total) >
            (getters.poAmountRemaining +
                getters.poAmountRemaining * rootGetters['doc_viewer_settings/poTotalDeviationPercentAllowed'] / 100)
    }
}

export const mutations = {
    [DOC_PO_SET]: (state, val) => {
        state.purchaseOrder = val
    },
    [DOC_PO_SET_IS_MATCHING]: (state, val) => {
        state.isPoMatching = val
    },
    [DOC_PO_SET_IS_AUTO_MATCHING]: (state, val) => {
        state.isPoAutoMatching = val
    },
    [DOC_PO_SET_TOTAL_ALLOCATED]: (state, val) => {
        state.poTotalAllocated = val
    },
    [DOC_PO_SET_HAS_TOTAL_ALLOCATED_GREATER_THAN_DOC_TOTAL]: (state, val) => {
        state.hasTotalAllocatedGreaterThanDocTotal = val
    },
    [DOC_PO_SET_HAS_TOTAL_UNALLOCATED_GREATER_THAN_ALLOWED]: (state, val) => {
        state.hasTotalUnallocatedGreaterThanAllowed = val
    },
    [DOC_PO_RESET]: state => {
        state.purchaseOrder = null
        state.isPoMatching = false
        state.isPoAutoMatching = false
        state.poTotalAllocated = 0
        state.hasTotalAllocatedGreaterThanDocTotal = false
        state.hasTotalUnallocatedGreaterThanAllowed = false
    }
}

export const actions = {
    [DOC_PO_CHECK]: async({dispatch, commit, rootGetters}) => {
        if (!rootGetters['doc_viewer_settings/poCheckEnabled']) {
            return
        }
        commit(DOC_PO_SET_IS_MATCHING, true)
        const {error, data, _axios} = await documentApi.match({
            doc: rootGetters['doc_viewer/doc'],
            filter: MatchingExtensionTypes.PURCHASE_ORDER,
            requestId: DOC_PO_CHECK
        })
        if (error) {
            if (!axios.isCancel(_axios.error)) {
                commit(DOC_PO_SET_IS_MATCHING, false)
            }
            const noMatchFound = _.get(_axios, 'error.response.status') === 404
            if (noMatchFound) {
                commit(DOC_PO_SET, null)
            }
        } else {
            commit(DOC_PO_SET, data)
            if (_.get(data, 'uuid')) {
                dispatch(DOC_PO_AUTOMATCH, {uuid: data.uuid})
            }
            commit(DOC_PO_SET_IS_MATCHING, false)
        }
    },

    [DOC_PO_AUTOMATCH]: ({dispatch, commit}, {uuid}) => {
        commit(DOC_PO_SET_IS_AUTO_MATCHING, true)
        return axios.get(`purchase-orders/${uuid}/lines`)
            .then(async res => {
                const purchaseOrder = _.isPlainObject(_.get(res, 'data.data')) ? res.data.data : {}
                if (purchaseOrder) {
                    await dispatch(DOC_PO_ON_MATCH, {purchaseOrder})
                }
            })
            .finally(() => {
                commit(DOC_PO_SET_IS_AUTO_MATCHING, false)
            })
    },

    [DOC_PO_AUTOMATCH_LINES]: async({getters, rootGetters, dispatch}, {override = true}) => {
        const item = getters.purchaseOrder
        if (!item || !_.isArray(item.lines) ||
            rootGetters['doc_viewer_settings/poProcessLevel'] !== MatchingExtensionProcessLevel.LINES) {
            return
        }
        const matchLinesSettings = rootGetters['doc_viewer_settings/poMatchLinesBy']
        for (const [docLineIdx, docLine] of rootGetters['doc_viewer/lines'].entries()) {
            let docLineNotMatchedQty = getters.getNotMatchedPoQtyForLine(docLine)
            if (docLineNotMatchedQty === 0) {
                continue
            }

            const docLineMatchTo = []
            for (const itemLine of item.lines) {
                if (!checkIsLinesMatched({docLine, itemLine, settings: matchLinesSettings})) {
                    continue
                }
                const itemLineRemainingQty = getMatchingItemLineRemainingQty({itemLine})
                if (itemLineRemainingQty === 0) {
                    continue
                }
                const qty = Math.min(docLineNotMatchedQty, itemLineRemainingQty)
                const matchTo = buildDocumentLineMatchTo({item, itemLine, qty})
                docLineMatchTo.push(matchTo)

                // temporary update 'matchedTo' - to avoid matching the same item line for different doc lines
                if (!_.isArray(itemLine.matchedTo)) {
                    itemLine.matchedTo = []
                }
                itemLine.matchedTo.push(matchTo)

                docLineNotMatchedQty -= qty
                if (docLineNotMatchedQty <= 0) {
                    break
                }
            }

            await dispatch(DOC_PO_ON_MATCH_LINE, {
                docLineMatchTo,
                docLineIdx,
                override,
                confirm: false
            })
        }
    },

    [DOC_PO_ADJUST_LINE_PO]: async({getters, rootGetters, dispatch}, {docLineIdx}) => {
        const line = rootGetters['doc_viewer/lines'][docLineIdx]
        if (!line) {
            return
        }
        const docLineQty = getDocLineQtyToMatch(line)
        const matchedQty = getters.getLineMatchedQty(line)
        if (!_.isArray(line.ordered) || matchedQty <= docLineQty) {
            return
        }
        let extraMatchedQty = matchedQty - docLineQty
        let updatedOrders = _.cloneDeep(line.ordered)
        line.ordered.forEach((r, idx) => {
            if (extraMatchedQty > 0 && _.isNumber(r.qty) && r.qty > 0) {
                if (extraMatchedQty > r.qty) {
                    updatedOrders[idx].qty = 0
                    extraMatchedQty -= r.qty
                } else {
                    updatedOrders[idx].qty -= extraMatchedQty
                    extraMatchedQty = 0
                }
            }
        })
        updatedOrders = updatedOrders.filter(r => r.qty !== 0)
        await dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
            idx: docLineIdx,
            path: 'lines.ordered',
            data: updatedOrders
        }, {root: true})
    },

    [DOC_PO_ON_MATCH]: async({getters, dispatch, commit}, {purchaseOrder}) => {
        if (getters.purchaseOrder) {
            await dispatch(DOC_PO_ON_UNMATCH)
        }
        await dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {path: 'purchaseOrder', data: purchaseOrder}, {root: true}) // api requirements
        commit(DOC_PO_SET, purchaseOrder)

        // we need to auto-match lines before start overriding
        const onOverride = async() => {
            await dispatch(DOC_PO_AUTOMATCH_LINES, {override: true})
        }
        const onOverrideSkip = async() => {
            await dispatch(DOC_PO_AUTOMATCH_LINES, {override: false})
        }
        await dispatch(DOC_PO_RUN_DATA_OVERRIDE, {onOverride, onOverrideSkip})
    },

    [DOC_PO_ON_UNMATCH]: async({dispatch, commit, rootGetters}) => {
        await dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {path: 'purchaseOrder', data: null}, {root: true})
        const linesBatchUpdateData = []
        rootGetters['doc_viewer/lines'].forEach((line, idx) => {
            if (line && !line.isDeleted) {
                linesBatchUpdateData.push({idx, path: 'ordered', data: null})
            }
        })
        if (linesBatchUpdateData.length !== 0) {
            await dispatch(`doc_viewer/${DOC_LINES_BATCH_UPDATE}`, linesBatchUpdateData, {root: true})
        }
        commit(DOC_PO_SET, null)
    },

    [DOC_PO_ON_MATCH_LINE]: async({rootGetters, dispatch}, {
        docLineMatchTo = [],
        docLineIdx = 0,
        override = true,
        confirm = true
    }) => {
        const line = rootGetters['doc_viewer/lines'][docLineIdx]
        if (!line) {
            return
        }

        await dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
            idx: docLineIdx,
            path: 'lines.ordered',
            data: docLineMatchTo
        }, {root: true})

        const externalCode = line.sku
        const internalCode = docLineMatchTo[0] ? docLineMatchTo[0].sku : ''
        if (externalCode && internalCode && externalCode !== internalCode) {
            await dispatch(`doc_viewer/${DOC_LINE_ALTERNATIVE_ALERT}`, {
                lineIdx: docLineIdx,
                internalCode,
                externalCode
            }, {root: true})
            await dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
                idx: docLineIdx,
                prop: 'lines.sku',
                data: internalCode
            }, {root: true})
        } else if (!externalCode) {
            await dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
                idx: docLineIdx,
                prop: 'lines.sku',
                data: internalCode
            }, {root: true})
        }

        if (override) {
            dispatch(DOC_PO_RUN_DATA_OVERRIDE_FOR_LINE, {docLineMatchTo, docLineIdx, confirm})
        }
    },

    [DOC_PO_RUN_DATA_OVERRIDE]: async(
        {dispatch, commit, getters, rootGetters},
        {
            onOverride = () => {
            },
            onOverrideSkip = () => {
            }
        }
    ) => {
        if (!getters.purchaseOrder ||
            !_.isPlainObject(getters.purchaseOrder.override) ||
            rootGetters['doc_viewer_settings/poAutofill'] === MatchingExtensionAutofill.NEVER) {
            onOverrideSkip()
            return
        }
        const forceOverride = rootGetters['doc_viewer_settings/poAutofillForce']
        const rootOverrides = _.cloneDeep(getters.purchaseOrder.override) || {}
        const companyOverrides = _.get(getters.purchaseOrder, ['override', 'company'], {})
        const firstLineOverrides = _.get(getters.purchaseOrder, ['lines', 0, 'override'], {})
        delete rootOverrides.lines
        delete rootOverrides.company

        const overrideRootFields = async overrides => {
            const promises = []
            for (const [prop, data] of Object.entries(overrides)) {
                const isExistingProperty = rootGetters['doc_viewer/docWithNormalizedParts'].hasOwnProperty(prop)
                if (!isExistingProperty || (!forceOverride && !data)) {
                    continue
                }

                if (prop === 'custom') {
                    if (!isPlainObject(data)) {
                        continue
                    }
                    for (const customProp of Object.keys(data)) {
                        if (!forceOverride && !data[customProp]) {
                            continue
                        }
                        promises.push(
                            dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
                                path: `custom.${customProp}`,
                                data: data[customProp]
                            }, {root: true})
                        )
                    }
                } else {
                    promises.push(dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {path: prop, data}, {root: true}))
                }
            }
            await Promise.all(promises)
        }

        const overrideCompanyFields = async overrides => {
            const promises = []
            for (const [prop, data] of Object.entries(overrides)) {
                if (!forceOverride && !data) {
                    continue
                }
                if (prop === 'custom') {
                    if (!_.isPlainObject(data)) {
                        continue
                    }
                    for (const customProp of Object.keys(data)) {
                        if (!forceOverride && !data[customProp]) {
                            continue
                        }
                        promises.push(
                            dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
                                path: `company.custom.${customProp}`,
                                data: data[customProp]
                            }, {root: true})
                        )
                    }
                } else {
                    promises.push(dispatch(`doc_viewer/${DOC_FIELD_UPDATE}`, {
                        path: `company.${prop}`,
                        data
                    }, {root: true}))
                }
            }
            const supplierId = _.get(companyOverrides, 'supplier.uuid')
            if (supplierId) {
                dispatch(`doc_viewer/${DOC_FILL_SUPPLIER}`, supplierId, {root: true})
            }
            await Promise.all(promises)
        }

        const overrideData = async() => {
            await Promise.all([
                overrideCompanyFields(companyOverrides),
                overrideRootFields(firstLineOverrides) // 1. overriding with the first line data
            ])
            await overrideRootFields(rootOverrides) // 2. overriding with root data (higher priority)
            onOverride()
        }

        if (rootGetters['doc_viewer_settings/poAutofill'] === MatchingExtensionAutofill.ALWAYS) {
            await overrideData()
        } else if (rootGetters['doc_viewer_settings/poAutofill'] === MatchingExtensionAutofill.ASK) {
            withConfirmation({
                onConfirm: overrideData,
                onCancel: onOverrideSkip,
                title: 'Update document fields from Purchase Order data',
                description: 'Do you want to update all the document fields <br/> to match this Purchase Order?',
                confirmBtnText: 'Yes',
                cancelBtnText: 'Cancel'
            })
        } else {
            onOverrideSkip()
        }
    },

    [DOC_PO_RUN_DATA_OVERRIDE_FOR_LINE]: async({dispatch, commit, getters, rootGetters}, {
        docLineMatchTo,
        docLineIdx,
        confirm = true
    }) => {
        const line = rootGetters['doc_viewer/lines'][docLineIdx]
        if (!line ||
            !docLineMatchTo[0] ||
            !_.isPlainObject(docLineMatchTo[0].override) ||
            rootGetters['doc_viewer_settings/poAutofill'] === MatchingExtensionAutofill.NEVER) {
            return
        }
        const forceOverride = rootGetters['doc_viewer_settings/poAutofillForce']

        const overrideLineData = async() => {
            const lineBatchUpdateData = []
            const lineOverrides = docLineMatchTo[0].override // since there can be multiple matches, we use overrides of the first one
            for (const [prop, data] of Object.entries(lineOverrides)) {
                if (!forceOverride && !data) {
                    continue
                }

                if (prop === 'custom') {
                    if (!_.isPlainObject(data)) {
                        continue
                    }
                    for (const customProp of Object.keys(data)) {
                        if (!forceOverride && !data[customProp]) {
                            continue
                        }
                        lineBatchUpdateData.push({
                            path: `custom.${customProp}`,
                            data: data[customProp],
                            idx: docLineIdx
                        })
                    }
                } else {
                    lineBatchUpdateData.push({path: prop, idx: docLineIdx, data})
                }
            }

            const itemId = _.get(lineOverrides, 'item.uuid')
            if (!line.item_uuid && itemId) {
                lineBatchUpdateData.push({path: 'item_uuid', idx: docLineIdx, data: itemId})
            }

            if (lineBatchUpdateData.length !== 0) {
                await dispatch(`doc_viewer/${DOC_LINES_BATCH_UPDATE}`, lineBatchUpdateData, {root: true})
            }
        }

        if (!confirm || rootGetters['doc_viewer_settings/poAutofill'] === MatchingExtensionAutofill.ALWAYS) {
            await overrideLineData()
        } else if (rootGetters['doc_viewer_settings/poAutofill'] === MatchingExtensionAutofill.ASK) {
            withConfirmation({
                title: `Update line ${docLineIdx + 1} from Purchase Order data`,
                description: `Do you want to update line ${docLineIdx + 1} data <br/> to match this Purchase Order?`,
                onConfirm: overrideLineData
            })
        }
    },

    [DOC_PO_FETCH_TOTAL_ALLOCATED]: async({commit, getters}) => {
        if (!getters.purchaseOrder || !getters.purchaseOrder.uuid) {
            return
        }
        return axios.get(`purchase-orders/${getters.purchaseOrder.uuid}/totalAllocated`)
            .then(res => {
                commit(DOC_PO_SET_TOTAL_ALLOCATED, res && res.data && _.isNumber(res.data.total) ? res.data.total : 0)
            })
            .catch(() => {
                commit(DOC_PO_SET_TOTAL_ALLOCATED, 0)
            })
    }
}

export default {
    state,
    getters,
    mutations,
    actions,
    namespaced: true
}
